import {
  Uniform,
  Vector2,
  RawShaderMaterial,
  GLSL3,
  GLSL1,
  FrontSide,
} from "three";
import getVertexShader from "./getVertexShader";
import getFragmentShader from "./getFragmentShader";
import { GRID_SIZE } from "../../constants";
import hasWebgl2 from "../../helpers/hasWebgl2";
import translateVertex from "./translateVertex";
import translateFragment from "./translateFragment";

function getDefaultUniforms({ antialias }) {
  return {
    u_height: new Uniform(260),
    u_width: new Uniform(1000),
    u_is_raycasting: new Uniform(false),
    u_grid: new Uniform(new Vector2(2 ** GRID_SIZE, 1)),
    u_x_start: new Uniform(new Vector2(0, 0)),
    u_x_dist: new Uniform(250000000),
    u_resolution: new Uniform(1),
    u_min_y: new Uniform(0),
    u_max_y: new Uniform(1),
    u_raycast_offset: new Uniform(0),
    u_y0: new Uniform(-100),
    u_y1: new Uniform(100),
    u_antialias: new Uniform(antialias ? 1 : 0),
  };
}

function createMaterial(uniforms, vShader, fShader, rest = {}) {
  const isWebgl2 = hasWebgl2();

  return new RawShaderMaterial({
    vertexShader: translateVertex(isWebgl2, vShader),
    fragmentShader: translateFragment(isWebgl2, fShader),
    uniforms,
    transparent: false,
    glslVersion: isWebgl2 ? GLSL3 : GLSL1,
    forceSinglePass: true,
    side: FrontSide,
    depthTest: true,
    depthWrite: true,
    ...rest,
  });
}

function getMaterialWithUniforms(conf = {}) {
  const { disableRaycast, textureConf, texture, antialias, ...rest } = conf;
  const u = getDefaultUniforms({ antialias });
  const meshConf = {};

  function compileMaterial(isPoints) {
    const fShader = getFragmentShader(u, isPoints, conf.alphaTest);
    const vShader = getVertexShader(conf, u, isPoints);
    return createMaterial(u, vShader, fShader, rest);
  }

  const obj = {
    // mesh conf
    setOffset: (o) => setMeshConf("offset", o),
    setPaddingX: (px) => setMeshConf("paddingX", px),
    setPaddingY: (py) => setMeshConf("paddingY", py),
    setWidth: (w) => setMeshConf("width", w),
    setArrowPointOffset: (o) => setMeshConf("arrowPointOffset", o),
    setBorderWidth: (b) => setMeshConf("borderWidth", b),
    setRaycastBorderWidth: (b) => setMeshConf("raycastBorderWidth", b),
    // uniforms
    setPosYMap: (posYMap) => setUniform("u_pos_y_map", posYMap),
    setPosXMap: (posXMap) => setUniform("u_pos_x_map", posXMap),
    setMinY: (y) => setUniform("u_min_y", y),
    setMaxY: (y) => setUniform("u_max_y", y),
    setPointSize: (ps) => setUniform("u_point_size", ps),
    setOffsetXMap: (o) => setUniform("u_offset_x_map", o),
    setOffsetYMap: (o) => setUniform("u_offset_y_map", o),
    setColorMap: (c) => setUniform("u_color_map", c),
    setTexture: (t) => setUniform("u_texture", t),
    setTextureCoordMap: (t) => setUniform("u_texture_coord_map", t),
    setTextureUvMap: (t) => setUniform("u_uv_map", t),
    setRaycastOffset: (x) => setUniform("u_raycast_offset", x),
    // getters
    getMeshConf: () => meshConf,
    getUniforms: () => u,
    //
    compileMaterial,
    uniforms: u,
    disableRaycast,
  };

  function setMeshConf(name, val) {
    meshConf[name] = val;
    return obj;
  }

  function setUniform(name, val) {
    u[name] = new Uniform(val);
    return obj;
  }

  return obj;
}

export default getMaterialWithUniforms;
